import { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import jobsAPI from '../api/jobs';

const Jobs = () => {
  // State management
  const [jobTitles, setJobTitles] = useState([]);
  const [offers, setOffers] = useState([]);
  const [selectedJobTitles, setSelectedJobTitles] = useState([]);
  const [appliedOffers, setAppliedOffers] = useState(new Set());
  const [expandedOffers, setExpandedOffers] = useState({});
  const [positionDetails, setPositionDetails] = useState({});
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadingPositions, setLoadingPositions] = useState({});
  const [applyingOffers, setApplyingOffers] = useState(new Set());
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreOffers, setHasMoreOffers] = useState(true);
  
  // Messages
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const { currentUser } = useAuth();
  
  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);
  
  const loadInitialData = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Load job titles and initial offers
      const [jobTitlesResponse, offersResponse] = await Promise.all([
        jobsAPI.getJobTitles(),
        jobsAPI.getOffers(null, 1, 20)
      ]);
      
      setJobTitles(jobTitlesResponse.data || []);
      setOffers(offersResponse.data || []);
      setCurrentPage(1);
      setHasMoreOffers((offersResponse.data || []).length === 20);
      
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('שגיאה בטעינת הנתונים. אנא נסה שנית.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle job title selection
  const handleJobTitleChange = async (jobTitleId) => {
    const newSelectedTitles = selectedJobTitles.includes(jobTitleId)
      ? selectedJobTitles.filter(id => id !== jobTitleId)
      : [...selectedJobTitles, jobTitleId];
    
    setSelectedJobTitles(newSelectedTitles);
    
    // Reload offers with new filter
    setIsLoading(true);
    setError('');
    
    try {
      const response = await jobsAPI.getOffers(
        newSelectedTitles.length > 0 ? newSelectedTitles : null, 
        1, 
        20
      );
      
      setOffers(response.data || []);
      setCurrentPage(1);
      setHasMoreOffers((response.data || []).length === 20);
      
    } catch (err) {
      console.error('Error filtering offers:', err);
      setError('שגיאה בסינון המשרות. אנא נסה שנית.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load more offers (pagination)
  const loadMoreOffers = async () => {
    if (!hasMoreOffers || isLoadingMore) return;
    
    setIsLoadingMore(true);
    
    try {
      const nextPage = currentPage + 1;
      const response = await jobsAPI.getOffers(
        selectedJobTitles.length > 0 ? selectedJobTitles : null,
        nextPage,
        20
      );
      
      const newOffers = response.data || [];
      setOffers(prevOffers => [...prevOffers, ...newOffers]);
      setCurrentPage(nextPage);
      setHasMoreOffers(newOffers.length === 20);
      
    } catch (err) {
      console.error('Error loading more offers:', err);
      setError('שגיאה בטעינת משרות נוספות. אנא נסה שנית.');
    } finally {
      setIsLoadingMore(false);
    }
  };
  
  // Toggle offer expansion and load position details
  const toggleOfferExpansion = async (offerId) => {
    const isExpanding = !expandedOffers[offerId];
    
    setExpandedOffers(prev => ({
      ...prev,
      [offerId]: isExpanding
    }));
    
    // Load position details if expanding and not already loaded
    if (isExpanding && !positionDetails[offerId] && !loadingPositions[offerId]) {
      const offer = offers.find(o => o.id === offerId);
      if (offer?.position?.id) {
        setLoadingPositions(prev => ({ ...prev, [offerId]: true }));
        
        try {
          const details = await jobsAPI.getPositionDetails(offer.position.id);
          setPositionDetails(prev => ({
            ...prev,
            [offerId]: details
          }));
        } catch (error) {
          console.error('Error fetching position details:', error);
        } finally {
          setLoadingPositions(prev => ({ ...prev, [offerId]: false }));
        }
      }
    }
  };
  
  // Handle apply to offer
  const handleApply = async (offerId) => {
    if (appliedOffers.has(offerId) || applyingOffers.has(offerId)) {
      return;
    }
    
    setApplyingOffers(prev => new Set([...prev, offerId]));
    setError('');
    setSuccess('');
    
    try {
      await jobsAPI.applyToOffer(offerId);
      
      // Update applied offers
      setAppliedOffers(prev => new Set([...prev, offerId]));
      
      // Update offers list to reflect applied status
      setOffers(prevOffers => 
        prevOffers.map(offer => 
          offer.id === offerId 
            ? { ...offer, applied: true } 
            : offer
        )
      );
      
      setSuccess('הגשת מועמדות בוצעה בהצלחה');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Error applying to offer:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('שגיאה בהגשת מועמדות. אנא נסה שנית.');
      }
      
      // Clear error message after 3 seconds
      setTimeout(() => setError(''), 3000);
    } finally {
      setApplyingOffers(prev => {
        const newSet = new Set(prev);
        newSet.delete(offerId);
        return newSet;
      });
    }
  };
  
  // Clear filters
  const clearFilters = () => {
    setSelectedJobTitles([]);
    loadInitialData();
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-4">
        <h1 className="text-2xl font-bold mb-6">משרות</h1>
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        {/* Success Message */}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{success}</span>
          </div>
        )}
        
        {/* Job Title Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">סינון לפי תפקיד</h2>
          
          {isLoading && jobTitles.length === 0 ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {jobTitles.map(jobTitle => (
                <label
                  key={jobTitle.id}
                  className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer transition-colors ${
                    selectedJobTitles.includes(jobTitle.id)
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={selectedJobTitles.includes(jobTitle.id)}
                    onChange={() => handleJobTitleChange(jobTitle.id)}
                  />
                  {jobTitle.name}
                </label>
              ))}
            </div>
          )}
        </div>
        
        {/* Job Listings */}
        <div className="space-y-4">
          {isLoading && offers.length === 0 ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : offers.length > 0 ? (
            <>
              {offers.map(offer => (
                <div key={offer.id} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex justify-between items-center">
                    <div 
                      className="flex items-center cursor-pointer flex-1"
                      onClick={() => toggleOfferExpansion(offer.id)}
                    >
                      <button
                        className="ml-2 text-gray-500 focus:outline-none"
                        aria-label={expandedOffers[offer.id] ? 'כווץ פרטים' : 'הרחב פרטים'}
                      >
                        <svg
                          className={`h-5 w-5 transform transition-transform ${
                            expandedOffers[offer.id] ? 'rotate-180' : ''
                          }`}
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                        </svg>
                      </button>
                      <span className="text-yellow-500 text-xl mr-2">⭐</span>
                      <h3 className="text-lg font-medium">{offer.position?.title || ''}</h3>
                    </div>
                    
                    <button
                      type="button"
                      onClick={() => handleApply(offer.id)}
                      disabled={
                        offer.applied || 
                        appliedOffers.has(offer.id) || 
                        applyingOffers.has(offer.id)
                      }
                      className={`text-sm py-2 px-4 rounded-md font-medium transition-colors ${
                        offer.applied || appliedOffers.has(offer.id)
                          ? 'border border-gray-400 shadow-sm text-gray-600 bg-gray-300 cursor-not-allowed opacity-75'
                          : applyingOffers.has(offer.id)
                          ? 'border border-amber-400 shadow-sm text-amber-600 bg-amber-100 cursor-not-allowed'
                          : 'border border-amber-600 shadow-sm text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500'
                      }`}
                    >
                      {applyingOffers.has(offer.id) ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-amber-500 mr-2"></div>
                          מגיש...
                        </div>
                      ) : (
                        currentUser?.gender === 'female' ? 'הגישי' : 'הגש'
                      )}
                    </button>
                  </div>
                  
                  {expandedOffers[offer.id] && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="prose max-w-none">
                        <div className="mb-6">
                          <div className="flex items-center mb-2">
                            <span className="text-yellow-500 text-lg mr-2 leading-none">⭐</span>
                            <h4 className="text-md font-semibold leading-none">פרטי המשרה</h4>
                          </div>
                          <div className="text-right text-sm text-gray-700 leading-tight space-y-1">
                            <div>חברה: {offer.position?.company?.public_hidden_name || ''}</div>
                            {!offer.position?.is_discrete && offer.position?.location && 
                             typeof offer.position.location === 'string' && (
                              <div>מיקום: {offer.position.location}</div>
                            )}
                            <div>תחומים: {Array.isArray(offer.position?.job_titles) 
                              ? offer.position.job_titles.map(jt => jt.name).join(', ')
                              : ''}</div>
                          </div>
                        </div>
                        
                        {Array.isArray(offer.position?.positions_skills) && offer.position.positions_skills.length > 0 && (
                          <div className="mb-6">
                            <div className="flex items-center mb-2">
                              <span className="text-yellow-500 text-lg mr-2 leading-none">⭐</span>
                              <h4 className="text-md font-semibold leading-none">כישורים נדרשים</h4>
                            </div>
                            <div className="text-right text-sm text-gray-700 leading-tight space-y-1">
                              {offer.position.positions_skills.map((skill, index) => (
                                <div key={skill.skill_id || index}>
                                  {skill.name || ''}
                                  {(skill.importance && skill.importance > 3) ? ' (חובה)' : ''}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Loading indicator for position details */}
                        {loadingPositions[offer.id] && (
                          <div className="mb-4 flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500 mr-2"></div>
                            <span className="text-sm text-gray-600">טוען פרטי משרה...</span>
                          </div>
                        )}
                        
                        {/* Position details */}
                        {positionDetails[offer.id] && positionDetails[offer.id].outsource_data?.description && (
                          <div className="mb-6">
                            <div className="flex items-center mb-2">
                              <span className="text-yellow-500 text-lg mr-2 leading-none">⭐</span>
                              <h4 className="text-md font-semibold leading-none">תיאור המשרה</h4>
                            </div>
                            <div className="text-right text-sm text-gray-700 leading-tight whitespace-pre-wrap">
                              {positionDetails[offer.id].outsource_data.description}
                            </div>
                          </div>
                        )}
                        
                        {positionDetails[offer.id] && positionDetails[offer.id].outsource_data?.requirements && (
                          <div className="mb-6">
                            <div className="flex items-center mb-2">
                              <span className="text-yellow-500 text-lg mr-2 leading-none">⭐</span>
                              <h4 className="text-md font-semibold leading-none">דרישות המשרה</h4>
                            </div>
                            <div className="text-right text-sm text-gray-700 leading-tight whitespace-pre-wrap">
                              {positionDetails[offer.id].outsource_data.requirements}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Load More Button */}
              {hasMoreOffers && (
                <div className="flex justify-center mt-6">
                  <button
                    type="button"
                    onClick={loadMoreOffers}
                    disabled={isLoadingMore}
                    className="bg-white border border-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoadingMore ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 mr-2"></div>
                        טוען משרות נוספות...
                      </div>
                    ) : (
                      'טען עוד משרות'
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <p className="text-gray-500 mb-4">לא נמצאו משרות מתאימות</p>
              {selectedJobTitles.length > 0 && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  נקה סינון
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Jobs;
